import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Package, Plus, Pencil, Trash2, Sparkles, Link2, Search, ExternalLink } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PageHeader, EmptyState, Spinner } from "../components/common";
import { FormDialog } from "../components/FormDialog";
import { ShopeeSearch } from "../components/ShopeeSearch";
import { useResource } from "../hooks/useResource";
import { api, brl, NICHES } from "../lib/api";

const productFields = (suppliers) => [
  { name: "title", label: "Nome do produto", required: true },
  { name: "url", label: "Link do produto (Shopee ou loja)", type: "url", placeholder: "https://shopee.com.br/..." },
  { name: "price_from", label: "Preço original (R$)", type: "number", half: true },
  { name: "price_to", label: "Preço atual (R$)", type: "number", half: true },
  { name: "commission_pct", label: "Comissão (%)", type: "number", half: true },
  { name: "niche", label: "Nicho", type: "select", options: NICHES, default: "Outros", half: true },
  { name: "image_url", label: "URL da imagem", type: "url" },
  { name: "supplier_id", label: "Fornecedor / loja", type: "select", placeholder: "Nenhum", options: suppliers.map((x) => ({ value: x.id, label: x.name })) },
  { name: "notes", label: "Detalhes / diferenciais (usado pela IA)", type: "textarea", rows: 3 },
];

const ProductCard = ({ p, onEdit, onDelete }) => {
  const nav = useNavigate();
  return (
    <div className="card-lux overflow-hidden flex flex-col rise" data-testid={`product-card-${p.id}`}>
      <div className="aspect-[4/3] bg-zinc-900 relative">
        {p.image_url ? <img src={p.image_url} alt={p.title} className="h-full w-full object-cover" /> : <div className="h-full grid place-items-center"><Package className="h-10 w-10 text-zinc-700" /></div>}
        <span className="absolute top-3 left-3 badge-gold backdrop-blur">{p.niche}</span>
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <div className="font-semibold text-sm line-clamp-2">{p.title}</div>
        <div className="mt-2 flex items-baseline gap-2">
          {p.price_to != null && <span className="font-display text-xl font-extrabold gold-text">{brl(p.price_to)}</span>}
          {p.price_from != null && <span className="text-xs text-zinc-500 line-through">{brl(p.price_from)}</span>}
        </div>
        {p.commission_pct != null && <div className="text-[11px] text-emerald-400 mt-1">Comissão {p.commission_pct}%</div>}
        <div className="mt-auto pt-4 grid grid-cols-2 gap-2">
          <Button size="sm" className="btn-gold rounded-full" onClick={() => nav(`/gerador?product=${p.id}`)} data-testid={`product-generate-${p.id}`}><Sparkles className="h-3.5 w-3.5 mr-1" />Conteúdo</Button>
          <Button size="sm" variant="outline" className="rounded-full border-zinc-700" disabled={!p.url} onClick={() => nav(`/links?url=${encodeURIComponent(p.url)}&title=${encodeURIComponent(p.title)}`)} data-testid={`product-link-${p.id}`}><Link2 className="h-3.5 w-3.5 mr-1" />Link</Button>
        </div>
        <div className="flex justify-end gap-1 mt-2">
          {p.url && <a href={p.url} target="_blank" rel="noreferrer" className="p-2 text-zinc-500 hover:text-white"><ExternalLink className="h-4 w-4" /></a>}
          <button onClick={onEdit} className="p-2 text-zinc-500 hover:text-amber-400" data-testid={`product-edit-${p.id}`}><Pencil className="h-4 w-4" /></button>
          <button onClick={onDelete} className="p-2 text-zinc-500 hover:text-rose-400" data-testid={`product-delete-${p.id}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
    </div>
  );
};

export default function Products() {
  const r = useResource("products");
  const [dlg, setDlg] = useState({ open: false, item: null });
  const [q, setQ] = useState("");
  const [niche, setNiche] = useState("");
  const [sup, setSup] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  useEffect(() => { api.get("/suppliers").then((x) => setSuppliers(x.data)); }, []);
  const list = useMemo(() => r.items.filter((p) => (!niche || p.niche === niche) && (!sup || p.supplier_id === sup) && p.title.toLowerCase().includes(q.toLowerCase())), [r.items, q, niche, sup]);

  return (
    <div>
      <PageHeader eyebrow="Catálogo" title="Meus produtos" desc="Organize seus achadinhos e gere conteúdo ou links rastreáveis em um toque."
        action={<div className="flex gap-2"><ShopeeSearch onImported={r.load} /><Button className="btn-gold rounded-full h-11 px-5" onClick={() => setDlg({ open: true, item: null })} data-testid="product-add-button"><Plus className="h-4 w-4 mr-1" />Adicionar</Button></div>} />
      <div className="flex flex-col sm:flex-row gap-2 mb-5">
        <div className="relative flex-1"><Search className="h-4 w-4 absolute left-3 top-3 text-zinc-500" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar produto..." className="pl-9 bg-zinc-900 border-zinc-800" data-testid="product-search-input" /></div>
        <select className="field sm:w-48" value={niche} onChange={(e) => setNiche(e.target.value)} data-testid="product-niche-filter"><option value="">Todos os nichos</option>{NICHES.map((n) => <option key={n}>{n}</option>)}</select>
        <select className="field sm:w-48" value={sup} onChange={(e) => setSup(e.target.value)} data-testid="product-supplier-filter"><option value="">Todos os fornecedores</option>{suppliers.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</select>
      </div>
      {r.loading ? <Spinner /> : list.length === 0 ? (
        <EmptyState icon={Package} title="Nenhum produto ainda" desc="Adicione manualmente, importe da loja de um fornecedor ou busque ofertas na Shopee." />
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {list.map((p) => <ProductCard key={p.id} p={p} onEdit={() => setDlg({ open: true, item: p })} onDelete={() => r.remove(p.id)} />)}
        </div>
      )}
      <FormDialog open={dlg.open} onOpenChange={(o) => setDlg((s) => ({ ...s, open: o }))} title={dlg.item ? "Editar produto" : "Novo produto"}
        fields={productFields(suppliers)} initial={dlg.item || { niche: "Outros" }} testid="product-form" onSubmit={(b) => r.save({ ...b, shopee_item_id: dlg.item?.shopee_item_id || "" }, dlg.item?.id)} />
    </div>
  );
}
